import { Age } from '../diagnoses/choices/epidemiology/age';
import { Sex } from '../diagnoses/choices/sex';
import { Diagnosis } from '../diagnoses/diagnosis';
import { LocalizedFindingItem } from './localized-findings';
import { SymptomItem } from './symptoms';

/**
 * A search query for diagnoses.
 */
export class SearchQuery {
  public age: Age;
  public sex: Sex;

  public symptoms: SymptomItem[];
  public localizedFindings: LocalizedFindingItem[];
  public excludedDiagnoses: Diagnosis[];

  private _key: string | null = null;

  constructor({
    age,
    sex,
    symptoms = [],
    localizedFindings = [],
    excludedDiagnoses = []
  }: {
    age: Age;
    sex: Sex;
    symptoms?: SymptomItem[];
    localizedFindings?: LocalizedFindingItem[];
    excludedDiagnoses?: Diagnosis[];
  }) {
    this.age = age;
    this.sex = sex;
    this.symptoms = symptoms;
    this.localizedFindings = localizedFindings;
    this.excludedDiagnoses = excludedDiagnoses;
  }

  key(): string {
    // Return cached key if available
    if (this._key !== null) {
      return this._key;
    }

    // Build key components
    const age = this.age.id;
    const sex = this.sex.id;
    const symptoms = this.symptoms
      .map((c) => (c.symptom.id ?? 0) + '__' + (c.isHighlighted ? '1' : '0'))
      .sort();
    const localized = this.localizedFindings
      .map((c) => c.finding.key() + '__' + (c.isHighlighted ? '1' : '0'))
      .sort();
    const excluded = this.excludedDiagnoses.map((d) => d.id).sort();

    // Build the key once
    this._key =
      'search_query:' +
      age +
      ':' +
      sex +
      ':' +
      symptoms.join(',') +
      ':' +
      localized.join(',') +
      ':' +
      excluded.join(',');
    return this._key;
  }

  canSearch(): boolean {
    return this.symptoms.length > 0 || this.localizedFindings.length > 0;
  }

  hasExcludedDiagnoses(): boolean {
    return this.excludedDiagnoses.length > 0;
  }

  copyWith({
    age = undefined,
    sex = undefined,
    symptoms = undefined,
    localizedFindings = undefined,
    excludedDiagnoses = undefined
  }: {
    age?: Age;
    sex?: Sex;
    symptoms?: SymptomItem[];
    localizedFindings?: LocalizedFindingItem[];
    excludedDiagnoses?: Diagnosis[];
  }) {
    return new SearchQuery({
      age: age ?? this.age,
      sex: sex ?? this.sex,
      symptoms: symptoms ?? this.symptoms,
      localizedFindings: localizedFindings ?? this.localizedFindings,
      excludedDiagnoses: excludedDiagnoses ?? this.excludedDiagnoses
    });
  }

  toJson(): any {
    return {
      age: this.age.id,
      sex: this.sex.id,
      symptoms: this.symptoms.map((s) => s.toJson()),
      localized_findings: this.localizedFindings.map((lf) => lf.toJson()),
      excluded_diagnoses: this.excludedDiagnoses.map((d) => ({
        id: d.id,
        name: d.name
      }))
    };
  }
}
